import { useState } from 'react';
import { Lock, User, ArrowLeft } from 'lucide-react';
import { signIn, resetPassword, confirmResetPassword, confirmSignIn } from 'aws-amplify/auth';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import logger from '../../utils/logger';

type AuthView = 'signIn' | 'forgotPassword' | 'resetPassword' | 'confirmSignIn';

interface CustomSignInProps {
  onSuccess: () => void;
  onBack: () => void;
  initialEmail?: string;
  initialPassword?: string;
}

export function CustomSignIn({ onSuccess, onBack, initialEmail, initialPassword }: CustomSignInProps) {
  const mobile = useIsMobile();
  const headingSize = mobile ? '1.4rem' : '1.8rem';
  const [view, setView] = useState<AuthView>('signIn');
  const [username, setUsername] = useState(initialEmail ?? '');
  const [password, setPassword] = useState(initialPassword ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});

  const clearForm = () => {
    setError(null);
    setMessage(null);
    setFieldErrors({});
    setUsername('');
    setPassword('');
    setConfirmationCode('');
    setNewPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setFieldErrors({});

    // Custom validation
    const errors: { username?: string; password?: string } = {};
    if (!username.trim()) {
      errors.username = 'Username is required';
    }
    if (!password) {
      errors.password = 'Password is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    logger.info('Sign-in attempt', { username });
    try {
      const result = await signIn({ username, password });

      if (result.isSignedIn) {
        logger.info('Sign-in successful', { username });
        onSuccess();
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        logger.info('New password required', { username });
        setView('confirmSignIn');
        setMessage('You need to set a new password');
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        logger.warn('Custom challenge required', { username });
        setError('Custom challenge required. Please contact support.');
      } else {
        logger.warn('Additional authentication step required', { username, step: result.nextStep.signInStep });
        setError('Additional authentication step required.');
      }
    } catch (err: any) {
      logger.error('Sign-in failed', { username, error: err.message });
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    logger.info('Password reset requested', { username });
    try {
      await resetPassword({ username });
      logger.info('Password reset code sent', { username });
      setView('resetPassword');
      setMessage('Password reset code sent to your email');
    } catch (err: any) {
      logger.error('Password reset request failed', { username, error: err.message });
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    logger.info('Password reset confirmation', { username });
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      logger.info('Password reset successful', { username });
      setView('signIn');
      setMessage('Password reset successful. Please sign in.');
      setPassword('');
      setConfirmationCode('');
      setNewPassword('');
    } catch (err: any) {
      logger.error('Password reset failed', { username, error: err.message });
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    logger.info('Confirming sign-in with new password', { username });
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      logger.info('Sign-in confirmed successfully', { username });
      onSuccess();
    } catch (err: any) {
      logger.error('Sign-in confirmation failed', { username, error: err.message });
      setError(err.message || 'Failed to confirm sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {view === 'signIn' && (
        <>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'inline-flex', padding: '0.75rem', background: `${theme.colors.teal}1A`, borderRadius: '1.5rem', color: theme.colors.teal, alignSelf: 'center' }}>
              <Lock size={28} />
            </div>
            <h2 style={{ fontSize: headingSize, fontFamily: theme.fontFamily.display, fontWeight: theme.fontWeight.light, margin: 0 }}>
              Welcome <span style={{ fontStyle: 'italic', fontWeight: 'bold', fontFamily: theme.fontFamily.serif, color: theme.colors.teal }}>Back</span>
            </h2>
            <p style={{ color: theme.colors.muted, fontWeight: theme.fontWeight.light, margin: 0 }}>Enter your credentials to access your dream holiday.</p>
          </div>

          <div style={{ background: 'white', borderRadius: '2rem', padding: '1.75rem', boxShadow: theme.shadows.xl, border: `1px solid ${theme.colors.paper}`, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && (
              <div style={{ padding: '0.75rem', background: "rgba(239,68,68,0.1)", border: `1px solid ${theme.colors.error}`, borderRadius: '1rem', color: theme.colors.errorText, fontSize: theme.fontSize.sm }}>
                {error}
              </div>
            )}
            {message && (
              <div style={{ padding: '0.75rem', background: "rgba(16,185,129,0.1)", border: `1px solid ${theme.colors.success}`, borderRadius: '1rem', color: theme.colors.successText, fontSize: theme.fontSize.sm }}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <User style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="text"
                    placeholder="connie@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>Raffle Code</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="password"
                    placeholder="AIT-XXXXXX"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '1rem', background: loading ? theme.colors.muted : theme.colors.navy, color: 'white', borderRadius: theme.borderRadius.full, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: theme.shadows.button, opacity: loading ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.teal; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.navy; }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>

          <button
            onClick={onBack}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: theme.colors.muted, background: 'transparent', border: 'none', cursor: 'pointer', margin: '0 auto', fontSize: theme.fontSize.sm, fontWeight: theme.fontWeight.medium, transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.navy}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.muted}
          >
            <ArrowLeft size={16} /> Back to home
          </button>
        </>
      )}

      {view === 'forgotPassword' && (
        <>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'inline-flex', padding: '0.75rem', background: `${theme.colors.teal}1A`, borderRadius: '1.5rem', color: theme.colors.teal, alignSelf: 'center' }}>
              <Lock size={28} />
            </div>
            <h2 style={{ fontSize: headingSize, fontFamily: theme.fontFamily.display, fontWeight: theme.fontWeight.light, margin: 0 }}>
              Reset <span style={{ fontStyle: 'italic', fontWeight: 'bold', fontFamily: theme.fontFamily.serif, color: theme.colors.teal }}>Password</span>
            </h2>
            <p style={{ color: theme.colors.muted, fontWeight: theme.fontWeight.light, margin: 0 }}>Enter your email to receive a reset code.</p>
          </div>

          <div style={{ background: 'white', borderRadius: '2rem', padding: '1.75rem', boxShadow: theme.shadows.xl, border: `1px solid ${theme.colors.paper}`, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && (
              <div style={{ padding: '0.75rem', background: "rgba(239,68,68,0.1)", border: `1px solid ${theme.colors.error}`, borderRadius: '1rem', color: theme.colors.errorText, fontSize: theme.fontSize.sm }}>
                {error}
              </div>
            )}

            <form onSubmit={handleForgotPassword} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>Username</label>
                <div style={{ position: 'relative' }}>
                  <User style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="text"
                    placeholder="connie traveler"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '1rem', background: loading ? theme.colors.muted : theme.colors.navy, color: 'white', borderRadius: theme.borderRadius.full, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: theme.shadows.button, opacity: loading ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.teal; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.navy; }}
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          </div>

          <button
            onClick={() => { clearForm(); setView('signIn'); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: theme.colors.muted, background: 'transparent', border: 'none', cursor: 'pointer', margin: '0 auto', fontSize: theme.fontSize.sm, fontWeight: theme.fontWeight.medium, transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.navy}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.muted}
          >
            <ArrowLeft size={16} /> Back to sign in
          </button>
        </>
      )}

      {view === 'resetPassword' && (
        <>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'inline-flex', padding: '0.75rem', background: `${theme.colors.teal}1A`, borderRadius: '1.5rem', color: theme.colors.teal, alignSelf: 'center' }}>
              <Lock size={28} />
            </div>
            <h2 style={{ fontSize: headingSize, fontFamily: theme.fontFamily.display, fontWeight: theme.fontWeight.light, margin: 0 }}>
              New <span style={{ fontStyle: 'italic', fontWeight: 'bold', fontFamily: theme.fontFamily.serif, color: theme.colors.teal }}>Password</span>
            </h2>
            <p style={{ color: theme.colors.muted, fontWeight: theme.fontWeight.light, margin: 0 }}>Enter the code and your new password.</p>
          </div>

          <div style={{ background: 'white', borderRadius: '2rem', padding: '1.75rem', boxShadow: theme.shadows.xl, border: `1px solid ${theme.colors.paper}`, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && (
              <div style={{ padding: '0.75rem', background: "rgba(239,68,68,0.1)", border: `1px solid ${theme.colors.error}`, borderRadius: '1rem', color: theme.colors.errorText, fontSize: theme.fontSize.sm }}>
                {error}
              </div>
            )}
            {message && (
              <div style={{ padding: '0.75rem', background: "rgba(16,185,129,0.1)", border: `1px solid ${theme.colors.success}`, borderRadius: '1rem', color: theme.colors.successText, fontSize: theme.fontSize.sm }}>
                {message}
              </div>
            )}

            <form onSubmit={handleResetPassword} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>Confirmation Code</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="text"
                    placeholder="123456"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '1rem', background: loading ? theme.colors.muted : theme.colors.navy, color: 'white', borderRadius: theme.borderRadius.full, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: theme.shadows.button, opacity: loading ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.teal; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.navy; }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>

          <button
            onClick={() => { clearForm(); setView('signIn'); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: theme.colors.muted, background: 'transparent', border: 'none', cursor: 'pointer', margin: '0 auto', fontSize: theme.fontSize.sm, fontWeight: theme.fontWeight.medium, transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.navy}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.muted}
          >
            <ArrowLeft size={16} /> Back to sign in
          </button>
        </>
      )}

      {view === 'confirmSignIn' && (
        <>
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'inline-flex', padding: '0.75rem', background: `${theme.colors.teal}1A`, borderRadius: '1.5rem', color: theme.colors.teal, alignSelf: 'center' }}>
              <Lock size={28} />
            </div>
            <h2 style={{ fontSize: headingSize, fontFamily: theme.fontFamily.display, fontWeight: theme.fontWeight.light, margin: 0 }}>
              Set New <span style={{ fontStyle: 'italic', fontWeight: 'bold', fontFamily: theme.fontFamily.serif, color: theme.colors.teal }}>Password</span>
            </h2>
            <p style={{ color: theme.colors.muted, fontWeight: theme.fontWeight.light, margin: 0 }}>You need to set a new password to continue.</p>
          </div>

          <div style={{ background: 'white', borderRadius: '2rem', padding: '1.75rem', boxShadow: theme.shadows.xl, border: `1px solid ${theme.colors.paper}`, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && (
              <div style={{ padding: '0.75rem', background: "rgba(239,68,68,0.1)", border: `1px solid ${theme.colors.error}`, borderRadius: '1rem', color: theme.colors.errorText, fontSize: theme.fontSize.sm }}>
                {error}
              </div>
            )}
            {message && (
              <div style={{ padding: '0.75rem', background: "rgba(16,185,129,0.1)", border: `1px solid ${theme.colors.success}`, borderRadius: '1rem', color: theme.colors.successText, fontSize: theme.fontSize.sm }}>
                {message}
              </div>
            )}

            <form onSubmit={handleConfirmSignIn} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.colors.muted, marginLeft: '1rem' }}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: theme.colors.gray300 }} />
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    style={{ width: '100%', background: theme.colors.paper, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.full, padding: '0.75rem 1.5rem 0.75rem 3.5rem', outline: 'none', transition: 'all 0.2s', fontWeight: theme.fontWeight.medium }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.colors.teal; e.currentTarget.style.background = 'white'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.colors.border; e.currentTarget.style.background = theme.colors.paper; }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '1rem', background: loading ? theme.colors.muted : theme.colors.navy, color: 'white', borderRadius: theme.borderRadius.full, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: theme.shadows.button, opacity: loading ? 0.5 : 1 }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.teal; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = theme.colors.navy; }}
              >
                {loading ? 'Confirming...' : 'Set New Password'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
